<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <h2>Borrowed Tutus</h2>
            <v-btn to="/iabf/admin/costume-orders">
              &lt; Back to Costume Orders
            </v-btn>
            <v-btn to="/iabf/admin/costume-inventory" class="ml-2">
              Costume Inventory
            </v-btn>
            <v-btn to="/iabf/admin/inventory" class="ml-2">
              Festival inventory
            </v-btn>
            <v-btn to="/iabf/admin/tutu-inventory-print" class="ml-2" outlined color="primary">
              🖨 Printable list
            </v-btn>
            <vue-good-table
              :columns="columns"
              :rows="rows"
              :line-numbers="true"
              :search-options="{
                enabled: true,
                placeholder: 'Search dancers',
              }"
            >
              <div slot="emptystate">
                No borrowed tutus found!
              </div>
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field === 'assignedColor'">
                  <v-select
                    v-if="props.row.waltzColorOptions.length > 0"
                    v-model="props.row.assignedColor"
                    :items="[{ text: '(none)', value: '' }].concat(props.row.waltzColorOptions.map((c, i) => ({ text: (i+1) + '. ' + c + (waltzColorCounts[c] ? ' (' + waltzColorCounts[c] + ')' : ''), value: c })))"
                    dense
                    hide-details
                    @change="assignColor(props.row, 'assignedWaltzColor', props.row.assignedColor)"
                    :style="props.row.assignedColor ? { fontWeight: 'bold' } : {}"
                  ></v-select>
                </span>
                <span v-else-if="props.column.field === 'assignedTutu'">
                  <v-autocomplete
                    :value="props.row.assignedTutu"
                    :items="getInventoryForRow(props.row)"
                    dense
                    hide-details
                    clearable
                    placeholder="Select costume…"
                    @change="onAssignTutu(props.row, $event)"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title :style="{ color: fitStatusColor(item.status), fontSize: '0.9em' }">
                          {{ item.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  <div v-if="props.row.assignedTutu && tutuFitItems(props.row).length" style="font-size: 0.78em; margin-top: 3px; line-height: 1.7;">
                    <span
                      v-for="item in tutuFitItems(props.row)"
                      :key="item.label"
                      :style="{ marginRight: '6px', color: item.status === 'small' ? '#c62828' : item.status === 'large' ? '#e65100' : '#2e7d32' }"
                    >{{ item.label }}: {{ item.tutuVal }}<span v-if="item.diff !== null"> ({{ item.diff >= 0 ? '+' : '' }}{{ item.diff }})</span></span>
                  </div>
                  <v-btn
                    v-if="!props.row.assignedTutu"
                    x-small
                    text
                    :color="props.row.buyNewOrdered ? 'success' : ''"
                    class="mt-1"
                    @click="openBuyNew(props.row)"
                  >{{ props.row.buyNewOrdered ? '✓ Ordered' : 'Buy New' }}</v-btn>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>

    <!-- Buy New dialog -->
    <v-dialog v-model="buyNewDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>Order New Tutu for {{ buyNewRow && buyNewRow.dancer }}</v-card-title>
        <v-card-text>
          <div class="mb-3"><strong>Type:</strong> {{ buyNewRow && inventoryTypeMap[buyNewRow.type] }}</div>
          <v-text-field
            v-model="buyNewForm.name"
            label="Inventory label *"
            dense
            :rules="[v => !!v || 'Required']"
          ></v-text-field>
          <v-select
            v-model="buyNewForm.color"
            label="Color *"
            :items="colorOptions"
            dense
          ></v-select>
          <div v-if="buyNewRow && buyNewRow.measurements" class="mt-2 grey--text text--darken-1" style="font-size: 0.9em;">
            Dancer measurements: {{ buyNewRow.measurements }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="buyNewDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="buyNewSaving" :disabled="!buyNewForm.name || !buyNewForm.color" @click="confirmBuyNew">
            Order &amp; Assign
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style>
  h2 {
    font-family: Quattrocento;
    font-weight: 400;
    font-size: 39px;
    letter-spacing: -.02em;
    line-height: 1em;
  }

  .vgt-table, v-btn {
    font-family: Fanwood Text;
  }

  .vgt-table thead {
    font-size: 1.2em !important;
  }
</style>

<script>
  import 'vue-good-table/dist/vue-good-table.css'
  import { VueGoodTable } from 'vue-good-table'

  export default {
    components: {
      VueGoodTable
    },
    data () {
      return {
        columns: [
          { label: 'Type', field: 'type' },
          { label: 'Dancer', field: 'dancer' },
          { label: 'Color Choices', field: 'colorChoices' },
          { label: 'Assigned Color', field: 'assignedColor' },
          { label: 'Assigned Tutu', field: 'assignedTutu' },
          { label: 'Measurements (in)', field: 'measurements' }
        ],
        rows: [],
        colorOptions: ['White', 'Blue', 'Pink', 'Yellow', 'Purple', 'Green'],
        inventoryTypeMap: {
          'Colorful Pancake + Bodice': 'Pancake with Bodice',
          'White Pancake': 'Pancake',
          'White Romantic': 'Romantic'
        },
        buyNewDialog: false,
        buyNewRow: null,
        buyNewForm: { name: '', color: '' },
        buyNewSaving: false
      }
    },
    created () {
      this.$store.dispatch('loadCostumeInventory')
      this.$store.dispatch('loadIABFDancers').then(() => {
        this.$store.getters.loadedIABFDancers.forEach((dancer) => {
          this.addBorrowRows(dancer)
        })
      })
    },
    computed: {
      assignedTutuNames () {
        return new Set(this.rows.filter(r => r.assignedTutu).map(r => r.assignedTutu))
      },
      waltzColorCounts () {
        const counts = {}
        const dancers = this.$store.getters.loadedIABFDancers || []
        dancers.forEach(d => {
          if (d.assignedWaltzColor) {
            counts[d.assignedWaltzColor] = (counts[d.assignedWaltzColor] || 0) + 1
          }
          const woh = d.waltzOfHours || {}
          if (woh.participating && woh.tutuChoice === 'own' && woh.tutuColor && !d.assignedWaltzColor) {
            counts[woh.tutuColor] = (counts[woh.tutuColor] || 0) + 1
          }
        })
        return counts
      }
    },
    methods: {
      toInches (value, unit) {
        if (!value) return ''
        const num = parseFloat(value)
        if (isNaN(num)) return ''
        if (unit === 'cm') return (num / 2.54).toFixed(1)
        return num.toFixed(1)
      },
      formatMeasurements (m, unit) {
        const parts = [
          { label: 'B', val: this.toInches(m.bust, unit) },
          { label: 'W', val: this.toInches(m.waist, unit) },
          { label: 'H', val: this.toInches(m.hip, unit) },
          { label: '½G', val: this.toInches(m.halfGirth, unit) },
          { label: 'Ht', val: this.toInches(m.height, unit) }
        ].filter(p => p.val)
        return parts.map(p => p.label + ': ' + p.val).join(' / ')
      },
      dancerName (dancer) {
        return dancer.firstName + ' ' + (dancer.lastName ? dancer.lastName.charAt(0) + '.' : '')
      },
      addBorrowRows (dancer) {
        const woh = dancer.waltzOfHours || {}
        const aiw = dancer.aliceInWonderland || {}
        const gw = dancer.garlandWaltz || {}
        const m = dancer.sharedMeasurements || {}
        const unit = m.unit || 'inches'
        const name = this.dancerName(dancer)
        const waltzColorOptions = [woh.colorPref1, woh.colorPref2, woh.colorPref3].filter(c => c)
        const measurements = this.formatMeasurements(m, unit)

        const toIn = (v) => { const n = parseFloat(this.toInches(v, unit)); return isNaN(n) ? null : n }
        const dancerMeasurements = {
          dancerBustIn: toIn(m.bust),
          dancerWaistIn: toIn(m.waist),
          dancerHipIn: toIn(m.hip),
          dancerHalfGirthIn: toIn(m.halfGirth),
          dancerGirthIn: toIn(m.girth),
          dancerHeightIn: toIn(m.height)
        }

        // Waltz of Hours borrow
        if (woh.participating && woh.tutuChoice === 'borrow') {
          this.rows.push({
            ...dancerMeasurements,
            id: dancer.id,
            type: 'Colorful Pancake + Bodice',
            dancer: name,
            colorChoices: waltzColorOptions.map((c, i) => (i + 1) + '. ' + c).join(', '),
            waltzColorOptions: waltzColorOptions,
            assignedColor: dancer.assignedWaltzColor || '',
            assignedTutu: dancer.borrowedWaltzTutu || '',
            borrowedField: 'borrowedWaltzTutu',
            orderField: 'borrowedWaltzTutuOrdered',
            buyNewOrdered: dancer.borrowedWaltzTutuOrdered || false,
            measurements: measurements
          })
        }

        // Alice borrow
        if (aiw.participating && aiw.role && aiw.role !== 'Men & Jokers' && aiw.role !== 'Queen of Hearts' && aiw.tutuChoice === 'borrow') {
          this.rows.push({
            ...dancerMeasurements,
            id: dancer.id,
            type: aiw.role === 'Pointe' ? 'White Pancake' : 'White Romantic',
            dancer: name,
            colorChoices: '',
            waltzColorOptions: [],
            assignedColor: '',
            assignedTutu: dancer.borrowedAliceTutu || '',
            borrowedField: 'borrowedAliceTutu',
            orderField: 'borrowedAliceTutuOrdered',
            buyNewOrdered: dancer.borrowedAliceTutuOrdered || false,
            measurements: measurements
          })
        }

        // Garland Ladies (not shared with Alice) borrow
        if (gw.participating && gw.role === 'Ladies' && !gw.tutuSharedWithAlice && gw.tutuChoice === 'borrow') {
          this.rows.push({
            ...dancerMeasurements,
            id: dancer.id,
            type: 'White Romantic',
            dancer: name,
            colorChoices: '',
            waltzColorOptions: [],
            assignedColor: '',
            assignedTutu: dancer.borrowedGarlandTutu || '',
            borrowedField: 'borrowedGarlandTutu',
            orderField: 'borrowedGarlandTutuOrdered',
            buyNewOrdered: dancer.borrowedGarlandTutuOrdered || false,
            measurements: measurements
          })
        }
      },
      getInventoryForRow (row) {
        const matchType = this.inventoryTypeMap[row.type]
        const dancerWaist = row.dancerWaistIn
        const dancerHip = row.dancerHipIn

        const fitScore = (c) => {
          let score = 0
          let measured = false
          if (c.waist != null && dancerWaist != null) {
            measured = true
            const diff = c.waist - dancerWaist
            if (diff < -1) score += 2000      // too small — won't close
            else if (diff > 6) score += 300   // way too big
            else score += diff * 2
          }
          if (c.hip != null && dancerHip != null) {
            measured = true
            const diff = c.hip - dancerHip
            if (diff < -1) score += 2000      // too small
            else if (diff > 6) score += 300   // way too big
            else score += diff
          }
          return measured ? score : 500       // no measurements: sort to middle
        }

        return this.$store.getters.costumeInventory
          .filter(c => {
            if (matchType && c.type !== matchType) return false
            if (this.assignedTutuNames.has(c.name) && c.name !== row.assignedTutu) return false
            return true
          })
          .slice()
          .sort((a, b) => fitScore(a) - fitScore(b))
          .map(c => {
            const score = fitScore(c)
            const parts = [
              c.bust ? 'B: ' + c.bust : null,
              c.waist ? 'W: ' + c.waist : null,
              c.hip ? 'H: ' + c.hip : null,
              c.halfGirth ? '½G: ' + c.halfGirth : null,
              c.girth ? 'G: ' + c.girth : null,
              c.height ? 'Ht: ' + c.height : null
            ].filter(Boolean)
            const measText = parts.length ? ' · ' + parts.join(' ') : ''
            const flag = score >= 2000 ? ' · too small' : score >= 300 ? ' · very large' : ''
            const status = score >= 2000 ? 'small' : score >= 300 ? 'large' : score === 500 ? 'unknown' : 'ok'
            return {
              text: c.name + ' (' + c.color + ')' + measText + flag,
              value: c.name,
              status
            }
          })
      },
      fitStatusColor (status) {
        if (status === 'small') return '#c62828'
        if (status === 'large') return '#e65100'
        if (status === 'ok') return '#2e7d32'
        return undefined
      },
      tutuFitItems (row) {
        const tutu = this.$store.getters.costumeInventory.find(c => c.name === row.assignedTutu)
        if (!tutu) return []
        return [
          { label: 'B', tutuVal: tutu.bust, dancerVal: row.dancerBustIn },
          { label: 'W', tutuVal: tutu.waist, dancerVal: row.dancerWaistIn },
          { label: 'H', tutuVal: tutu.hip, dancerVal: row.dancerHipIn },
          { label: '½G', tutuVal: tutu.halfGirth, dancerVal: row.dancerHalfGirthIn },
          { label: 'G', tutuVal: tutu.girth, dancerVal: row.dancerGirthIn },
          { label: 'Ht', tutuVal: tutu.height, dancerVal: row.dancerHeightIn }
        ]
          .filter(c => c.tutuVal != null)
          .map(c => {
            const diff = c.dancerVal != null ? Math.round((c.tutuVal - c.dancerVal) * 10) / 10 : null
            const status = diff === null ? 'unknown' : diff < -1 ? 'small' : diff > 6 ? 'large' : 'ok'
            return { ...c, diff, status }
          })
      },
      assignColor (row, field, color) {
        this.$store.dispatch('assignCostumeColor', { id: row.id, field: field, color: color })
      },
      onAssignTutu (row, value) {
        const name = value || ''
        const idx = this.rows.findIndex(r => r.id === row.id && r.borrowedField === row.borrowedField)
        // Always read live row data — vue-good-table passes a stale clone via props.row
        const live = idx !== -1 ? this.rows[idx] : row

        let colorUpdate = {}
        if (name && live.waltzColorOptions.length > 0) {
          const tutu = this.$store.getters.costumeInventory.find(c => c.name === name)
          if (tutu && tutu.color) {
            colorUpdate = { assignedColor: tutu.color }
            this.$store.dispatch('assignCostumeColor', { id: live.id, field: 'assignedWaltzColor', color: tutu.color })
          }
        }

        // If there was a "buy new" order and the user is now picking a different tutu, clear the order
        // and delete the inventory item (identified by the previously assigned tutu name)
        let orderUpdate = {}
        if (live.buyNewOrdered && live.orderField && name !== live.assignedTutu) {
          this.$store.dispatch('assignCostumeColor', { id: live.id, field: live.orderField, color: false })
          orderUpdate = { buyNewOrdered: false }
          if (live.assignedTutu) {
            const item = this.$store.getters.costumeInventory.find(c => c.name === live.assignedTutu)
            if (item) this.$store.dispatch('deleteCostumeInventoryItem', item.id)
          }
        }

        if (idx !== -1) {
          this.$set(this.rows, idx, { ...this.rows[idx], assignedTutu: name, ...colorUpdate, ...orderUpdate })
        }
        this.$store.dispatch('assignCostumeColor', { id: live.id, field: live.borrowedField, color: name })
      },
      openBuyNew (row) {
        this.buyNewRow = row
        this.buyNewForm = {
          name: row.dancer + ' (Borrow)',
          color: row.assignedColor || row.waltzColorOptions[0] || 'White'
        }
        this.buyNewDialog = true
      },
      async confirmBuyNew () {
        if (!this.buyNewRow || !this.buyNewForm.name || !this.buyNewForm.color) return
        this.buyNewSaving = true
        const row = this.buyNewRow

        // Build inventory item from dancer's measurements (omit nulls)
        const inventoryItem = { id: null, name: this.buyNewForm.name, type: this.inventoryTypeMap[row.type] || row.type, color: this.buyNewForm.color }
        const mFields = { bust: row.dancerBustIn, waist: row.dancerWaistIn, hip: row.dancerHipIn, halfGirth: row.dancerHalfGirthIn, girth: row.dancerGirthIn, height: row.dancerHeightIn }
        Object.keys(mFields).forEach(k => { if (mFields[k] != null) inventoryItem[k] = mFields[k] })

        await this.$store.dispatch('saveCostumeInventoryItem', inventoryItem)

        // Flag the dancer doc so TutuOrder page shows this as a buy
        await this.$store.dispatch('assignCostumeColor', { id: row.id, field: row.orderField, color: true })

        // Auto-assign the new tutu and update the row
        const tutuName = this.buyNewForm.name
        const idx = this.rows.findIndex(r => r.id === row.id && r.borrowedField === row.borrowedField)
        let colorUpdate = {}
        if (row.waltzColorOptions.length > 0 && this.buyNewForm.color) {
          colorUpdate = { assignedColor: this.buyNewForm.color }
          this.$store.dispatch('assignCostumeColor', { id: row.id, field: 'assignedWaltzColor', color: this.buyNewForm.color })
        }
        if (idx !== -1) {
          this.$set(this.rows, idx, { ...this.rows[idx], assignedTutu: tutuName, buyNewOrdered: true, buyNewTutuName: tutuName, ...colorUpdate })
        }
        this.$store.dispatch('assignCostumeColor', { id: row.id, field: row.borrowedField, color: tutuName })

        this.buyNewSaving = false
        this.buyNewDialog = false
        this.buyNewRow = null
      }
    }
  }
</script>
